import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, ChevronRight } from 'lucide-react';
import { chatSeeds, chatUpdatedAt } from '../data/mockData';
import { useOfferings } from './OfferingsProvider';

// D/M/YYYY, the way dates are written in Singapore.
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const matchesQuery = (haystack, tokens) =>
  tokens.every((token) => haystack.includes(token));

// Every registered collection contributes its seeded threads, so adding a new
// kind of offering surfaces its chats here without touching this file. Sorted
// newest first, like any messaging inbox.
const buildChatEntries = (detailCollections) =>
  Object.entries(detailCollections)
    .flatMap(([type, items]) =>
      (items || [])
        .filter((item) => chatSeeds[item.id]?.length)
        .map((item) => {
          const seed = chatSeeds[item.id];
          const last = seed[seed.length - 1];
          const updatedAt = chatUpdatedAt[item.id] || '';
          return {
            id: item.id,
            type,
            title: item.title,
            image: item.image,
            lastMessage: last?.text.replace(/\n/g, ' ') || '',
            updatedAt,
            date: formatDate(updatedAt),
            haystack: [
              item.title,
              item.org,
              type,
              ...(seed || []).map((m) => m.text)
            ].filter(Boolean).join(' ').toLowerCase()
          };
        })
    )
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

export default function ChatInboxPanel({ isOpen, onClose, onSelectChat }) {
  const { detailCollections } = useOfferings();
  const entries = useMemo(() => buildChatEntries(detailCollections), [detailCollections]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchOpen(false);
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const visible = tokens.length
    ? entries.filter((entry) => matchesQuery(entry.haystack, tokens))
    : entries;

  const toggleSearch = () => {
    setSearchOpen((open) => {
      if (open) setSearchQuery('');
      return !open;
    });
  };

  return (
    <>
      {/* Dims whatever sits beside the panel. On a handset the panel covers the
          screen so this is never seen; in a desktop browser it frames the column. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-[59] bg-black/30 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      {/* The panel slides inside the app's own column, which clips it out of sight
          while it is closed rather than leaving it parked beside the phone. */}
      <div className="fixed inset-0 z-[60] pointer-events-none">
      <div className="relative mx-auto h-full max-w-[480px] overflow-hidden">
      <div
        data-testid="chat-inbox-panel"
        role="dialog"
        aria-modal="true"
        aria-label="All chats"
        className={`absolute inset-0 bg-white flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'
        }`}
      >
      <div className="px-2 pt-4 pb-3 border-b border-border/60 flex-shrink-0">
        <div className="flex items-center justify-between">
          <button
            type="button"
            data-testid="chat-inbox-close"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted flex-shrink-0"
          >
            <X className="h-5 w-5 text-[#0F172A]" />
          </button>
          <h2 className="text-base font-bold text-[#0F172A]">All chats</h2>
          <button
            type="button"
            data-testid="chat-inbox-search"
            aria-label={searchOpen ? 'Hide chat search' : 'Search chats'}
            aria-pressed={searchOpen}
            onClick={toggleSearch}
            className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${
              searchOpen ? 'bg-muted text-primary' : 'hover:bg-muted text-[#0F172A]'
            }`}
          >
            <Search className="h-4.5 w-4.5" />
          </button>
        </div>
        {searchOpen && (
          <div className="relative mt-3 px-2">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              ref={searchRef}
              type="search"
              data-testid="chat-inbox-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chats..."
              autoComplete="off"
              enterKeyHint="search"
              className="w-full h-10 rounded-full bg-muted/70 pl-9 pr-10 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            {searchQuery ? (
              <button
                type="button"
                data-testid="chat-inbox-search-clear"
                aria-label="Clear search"
                onClick={() => {
                  setSearchQuery('');
                  searchRef.current?.focus();
                }}
                className="absolute right-5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {visible.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-16 px-6" data-testid="chat-inbox-empty">
            {tokens.length
              ? `No chats match “${searchQuery.trim()}”.`
              : 'No conversations yet.'}
          </div>
        ) : (
          visible.map((entry) => (
            <button
              key={entry.id}
              type="button"
              data-testid={`chat-inbox-row-${entry.id}`}
              onClick={() => onSelectChat(entry)}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/40 hover:bg-muted/50 text-left"
            >
              <img src={entry.image} alt={entry.title} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-[#0F172A] truncate">{entry.title}</p>
                  <span className="text-[11px] text-muted-foreground shrink-0">{entry.date}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{entry.lastMessage}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
            </button>
          ))
        )}
      </div>
      </div>
      </div>
      </div>
    </>
  );
}
